import { AuthUser } from '../core/state/auth-store';
import { confirmLeave, hasLeaveGuard } from './leave-guard';
import { el, clear } from './dom';
import { shopApi } from './game-api';
import { renderFarm } from './views/farm';
import { closeSleepOverlay } from './views/farm/building-menu';
import { renderShop } from './views/shop';
import { renderArena } from './views/arena-br';
import { renderQuests } from './views/quests';
import { renderLeaderboards } from './views/leaderboards';
import { renderAchievements } from './views/achievements';
import { renderAdmin } from './views/admin';
import type { GameCtx, ViewRenderer, ViewTeardown } from './types';
import { openExchangeModal } from './exchange-modal';
import { mountChat, type ChatHandle } from './chat-widget';
import { openSettings, loadSettings, saveSettings, type GameSettings, type SettingsTab } from './settings-modal';
import { gicon, type IconName } from './ui/icons';
import { maybeShowLegend } from './legend-comic';
import { isShortViewport, onLayoutChange } from './mobile/layout';
import { initAudio, playSfx, setAudioView, stopAudio, type AudioView } from './audio/audio';

let currentTeardown: (() => void) | null = null;

const TABS: { id: string; label: string; icon: IconName; render: ViewRenderer }[] = [
  { id: 'farm', label: 'Farm', icon: 'leaf', render: renderFarm },
  { id: 'shop', label: 'Shop', icon: 'shroom', render: renderShop },
  { id: 'arena', label: 'Arena', icon: 'swords', render: renderArena },
  { id: 'quests', label: 'Quests', icon: 'check', render: renderQuests },
  { id: 'ranks', label: 'Ranks', icon: 'crown', render: renderLeaderboards },
  { id: 'awards', label: 'Awards', icon: 'trophy', render: renderAchievements },
];

/**
 * Which tab the player was on, so a reload puts them back (defect 19, rule R1).
 *
 * The boot decision used to be one hardcoded `setTab('farm')`, so refreshing while
 * searching for an arena match did not merely cancel the search — it moved the player
 * to a different screen and never told them why. This is the whole of the fix; the
 * arena's own resume markers do the rest once the arena view is mounted.
 *
 * sessionStorage, matching the arena markers: "where I am" belongs to this browser
 * tab. In localStorage a second tab would open into the arena because the first one
 * happened to be fighting.
 *
 * Storage can throw outright (private mode, blocked site data), and a player whose
 * browser blocks it simply gets the old behaviour rather than an error.
 */
export const LAST_TAB_KEY = 'tb.tab';

export function readLastTab(): string | null {
  try {
    const v = sessionStorage.getItem(LAST_TAB_KEY);
    return v && /^[a-z]+$/.test(v) ? v : null;
  } catch { return null; }
}

export function writeLastTab(id: string): void {
  try { sessionStorage.setItem(LAST_TAB_KEY, id); } catch { /* nothing to lose */ }
}

/** The Admin tab is appended only for admins (server still guards every call). */
const ADMIN_TAB = { id: 'admin', label: 'Admin', icon: 'gear' as IconName, render: renderAdmin };

export function mountGame(root: HTMLElement, user: AuthUser, onLogout: () => void): () => void {
  clear(root);

  const tabs = user.role === 'ADMIN' ? [...TABS, ADMIN_TAB] : TABS;

  // ─── HUD chips — ONLY the two currencies live in the header (owner rule):
  // Coins + Shrooms (the premium buying currency; unrelated to foraged
  // mushrooms). Everything farmed/mined lives in the in-game Bag instead.
  const refreshHud = async () => {
    try {
      const w = await shopApi.wallet();
      coins.value.textContent = w.coins.toLocaleString();
      shrooms.value.textContent = w.shrooms.toLocaleString();
      xp.set(w.level, w.xp, w.xpIntoLevel, w.xpForNextLevel, w.isMaxLevel);
    } catch { /* HUD is best-effort */ }
  };
  const ctx: GameCtx = { user, refreshHud };

  // Coins → open the Shrooms→Coins exchange. Shrooms → jump to the shop's buy
  // section. Both chips are buttons (clickable, hover-lit).
  const coins = hudChip('/sprites/ui/currency/coin.png', 'text-coin',
    { title: 'Trade Shrooms for Coins', onClick: () => openExchangeModal(refreshHud) });
  const shrooms = hudChip('/sprites/ui/currency/shroom.png', 'text-shroom',
    { title: 'Buy more Shrooms', onClick: () => setTab('shop') });

  // Level + XP: ambient progress, always on screen (every tab), refreshed by the
  // same wallet call the currency chips use. See design/gdd/hud-bars.md.
  const xp = xpChip();

  // World chat belongs to the FARM only. The widget is mounted once on the shell
  // (so its socket + history survive tab switches) but it is hidden on every other
  // view: as a fixed z-[55] bubble it floated over the Shop / Arena / Quests /
  // Awards / Admin pages and covered their content. The header button follows the
  // same rule rather than becoming a dead button.
  let chat: ChatHandle | null = null;
  // `tb-hbtn`, not `tb-pixel-btn`: the plank (surface + 2px border + inset bevel) is
  // deliberately gone from the header's icon controls. `tb-pixel-btn` is still the
  // right button everywhere else in the game, so the box is dropped by using a
  // different class rather than by hollowing out the shared one.
  const chatBtn = chatButton('tb-hbtn relative p-1.5', () => chat?.toggle());

  // ─── Top bar (pixel-styled: game font, crisp icons, wood-dark plank look) ─────
  // The wordmark lockup — mushroom to the LEFT of the type — so the in-game header,
  // the auth board and the marketing site all show the same mark. The previous
  // /ui/logo.png was a different wordmark, which is what made the branding look
  // inconsistent between the site and the game.
  const logo = el('img', 'tb-logo h-9 w-auto mr-1', { src: '/ui/logo_wordmark.png', alt: 'Terraboma', style: 'image-rendering:pixelated' });
  // ─── Tab nav ────────────────────────────────────────────────────────────────
  // Declared BEFORE the header because on a short viewport it is folded INTO it
  // (see applyChromeLayout) — the header needs it as a child at construction time
  // rather than being re-parented from underneath.
  const nav = el('nav', 'game-font tb-nav flex gap-1 px-4 pt-3 shrink-0');
  const spacer = el('div', 'tb-spacer flex-1');
  // Two explicit GROUPS with the read-outs on the left and the controls on the
  // right, rather than seven siblings separated only by one gap: the owner read the
  // phone header as "not consistent", and an unbroken run of chips-then-nav-then-
  // buttons is what made the wallet and the controls look like one jumbled strip.
  // `.tb-hgroup` also gives the phone stylesheet a single hook for the tight
  // intra-group gap versus the wider gap BETWEEN groups.
  const bar = el('header', 'game-font tb-header shrink-0 z-30 flex items-center gap-3 px-5 py-2.5',
    {},
    logo,
    el('div', 'tb-hgroup flex items-center gap-2', {}, coins.wrap, shrooms.wrap, xp.wrap),
    spacer,
    // The name was an inert <span> for months and players kept clicking it: it is the
    // one place in the shell that names YOU, so it must be the way to your profile.
    // A <button> (not a span with onClick) so it is tab-reachable and inherits the
    // header's existing hover glow + focus ring from `.tb-hbtn`.
    el('button', 'tb-username tb-hbtn text-[13px] text-[#e9d6bd]/80 hidden sm:block px-1.5 py-1',
      { title: 'Your profile', onClick: () => openGameSettings('profile') }, user.username ?? user.email),
    el('div', 'tb-hgroup flex items-center gap-2', {},
      chatBtn.el,
      // 16px on every header glyph: the gear was 16, the caret 14 and the tab icons
      // 15, which is exactly the kind of drift that reads as "unfinished" at a
      // glance even when nobody can name what is wrong.
      el('button', 'tb-hbtn p-1.5', { title: 'Settings', onClick: () => openGameSettings() }, gicon('gear', 16)),
      // Hide-the-header toggle. On a landscape phone the header + tab nav eat ~90px
      // of a 390px-tall screen; this collapses both in one tap (and the floating
      // "Show UI" control brings them back). It is the same immersive flag the
      // settings modal drives, so the two never disagree.
      el('button', 'tb-hbtn p-1.5', { title: 'Hide the header', onClick: () => setImmersive(true) }, gicon('caretUp', 16)),
      // Hidden on a phone (app.css) — signing out also lives in the settings modal,
      // and the folded header row has no width to spare.
      // KEEPS its plank: it is the one WORDED, destructive action in the row. Bare
      // words on the header gradient would read as a label rather than a button,
      // and the boxes the owner objected to were the five icon widgets.
      el('button', 'tb-logout tb-pixel-btn px-3 py-1.5 text-sm', { onClick: onLogout }, 'Sign out')));

  // ─── Content ─────────────────────────────────────────────────────────────────
  const content = el('main', 'flex-1 min-h-0');
  // `game-font` on the SHELL, not just the header and nav.
  //
  // It used to sit on those two only, so the chrome spoke in the game's face while
  // every tab BODY — quests, shop, arena, awards, admin — inherited the browser's
  // system font from `body`. That is why those screens kept looking like a
  // different product however many times the font was changed: nothing in them
  // ever asked for it.
  const shell = el('div', 'game-font h-screen flex flex-col overflow-hidden', {}, bar, nav, content);
  root.appendChild(shell);

  // farm/arena are full-bleed canvas views; the other tabs are padded + scrollable
  // but now use the FULL width of the screen (no 880px centered column).
  const FULL_BLEED = new Set(['farm', 'arena']);   // game canvas views fill the screen
  const contentClass = (id: string) =>
    FULL_BLEED.has(id) ? 'flex-1 min-h-0' : 'flex-1 min-h-0 overflow-auto px-6 py-6 w-full';

  let activeView: ViewTeardown | null = null;
  let activeTab = 'farm';
  const tabButtons = new Map<string, HTMLButtonElement>();

  /**
   * Switch tabs, asking first if the current view has something to lose.
   *
   * The guard is the only reason this is split in two: the in-game confirm card
   * answers asynchronously (the browser's blocking `confirm` is banned here —
   * views/farm/no-browser-dialogs.test.ts), so the decision cannot be a boolean
   * in front of the teardown. The common case still costs nothing: with no guard
   * set, confirmLeave resolves already-resolved.
   */
  function setTab(id: string) {
    if (id === activeTab || !hasLeaveGuard()) { applyTab(id); return; }
    void confirmLeave().then((ok) => { if (ok) applyTab(id); });
  }

  function applyTab(id: string) {
    activeView?.();
    clear(content);
    content.className = contentClass(id);
    activeTab = tabs.find((t) => t.id === id) ? id : tabs[0].id;
    // AFTER the fallback, never before: storing the id as asked would remember
    // 'admin' for a player who lost the role, and the next boot would read back a
    // tab that no longer exists. What is stored is always a tab that resolved.
    writeLastTab(activeTab);
    for (const [tid, btn] of tabButtons) btn.className = tabClass(tid === activeTab);
    // The music follows the VIEW (owner: "farm sound continues everywhere"). This is
    // the single place that declares where the player is; audio/track.ts decides what
    // that means — farm → day/night, everything else → silence, and an arena match
    // overrides both while it is on screen.
    setAudioView(activeTab as AudioView);
    activeView = (tabs.find((t) => t.id === activeTab) ?? tabs[0]).render(content, ctx);
    applyChatVisibility();
  }

  /**
   * The chat lives on the farm and nowhere else — see the note by `chatBtn`. Two
   * conditions gate it: the player's "Show chat" setting and the active tab.
   */
  function applyChatVisibility() {
    const show = settings.showChat && activeTab === 'farm';
    chat?.setVisible(show);
    chatBtn.el.style.display = show ? '' : 'none';
    immersiveChatBtn.el.style.display = show ? '' : 'none';
  }

  for (const t of tabs) {
    // The label is a SPAN, not a bare text node, so the folded phone header can
    // hide it on inactive tabs with CSS alone (`.tb-nav-inline .tb-tab-off
    // .tb-tab-label`). title + aria-label keep the icon-only tabs named.
    const btn = el('button', tabClass(false) + ' inline-flex items-center gap-1.5',
      { onClick: () => setTab(t.id), title: t.label, 'aria-label': t.label },
      gicon(t.icon, 16), el('span', 'tb-tab-label', {}, t.label));
    tabButtons.set(t.id, btn);
    nav.appendChild(btn);
  }

  /**
   * Short viewport → the tab nav moves INSIDE the header.
   *
   * A 63px header stacked on a 51px nav is 114px of chrome, 29% of a landscape
   * phone's 390px. Folded into one row the whole thing costs ~48px and the farm
   * gets the rest. This is the DEFAULT on a short screen — immersive mode is still
   * there for players who want even that back, but nobody should have to find a
   * setting before the game is playable.
   *
   * Re-parenting rather than duplicating: the tab buttons, their click handlers and
   * the active-tab styling stay the single set that `setTab` already writes to.
   */
  function applyChromeLayout() {
    const short = isShortViewport();
    if (short && nav.parentElement !== bar) bar.insertBefore(nav, spacer);
    if (!short && nav.parentElement !== shell) shell.insertBefore(nav, content);
    nav.classList.toggle('tb-nav-inline', short);
    // The canvas views size themselves from the content box, which just changed
    // height without a window resize — same signal immersive mode fires.
    window.dispatchEvent(new CustomEvent('tb-chrome-changed'));
  }
  const stopLayoutWatch = onLayoutChange(applyChromeLayout);
  applyChromeLayout();

  // ─── Settings + immersive (hide-chrome) mode ─────────────────────────────────
  let settings: GameSettings = loadSettings();
  // Floating controls visible only in immersive mode, so the player can reopen
  // settings / bring the bars back without the top chrome.
  const immersiveChatBtn = chatButton('tb-hbtn relative p-1.5', () => chat?.toggle());
  const immersiveBtns = el('div', 'tb-float fixed z-[56] flex gap-1.5',
    { style: 'top: max(0.5rem, env(safe-area-inset-top)); right: max(0.5rem, env(safe-area-inset-right));' },
    immersiveChatBtn.el,
    el('button', 'tb-hbtn p-1.5', { title: 'Settings', onClick: () => openGameSettings() }, gicon('gear', 16)),
    el('button', 'tb-hbtn px-2 py-1.5 text-xs inline-flex items-center gap-1', { title: 'Show bars', onClick: () => setImmersive(false) }, gicon('caretDown', 16), 'UI'));
  immersiveBtns.style.display = 'none';
  shell.appendChild(immersiveBtns);

  function applySettings() {
    bar.style.display = settings.immersive ? 'none' : '';
    nav.style.display = settings.immersive ? 'none' : '';
    immersiveBtns.style.display = settings.immersive ? 'flex' : 'none';
    // Views position their own floating chrome against the viewport; immersive mode
    // removes the header they were sized under, so they need to know. `data-immersive`
    // on <body> is the single flag (see the `[data-immersive]` rules in app.css).
    document.body.dataset.immersive = settings.immersive ? 'on' : '';
    applyChatVisibility();
    // Hiding the bars changes the content box without a window resize event — the
    // canvas views listen for this to re-fit (see farm.ts).
    window.dispatchEvent(new CustomEvent('tb-chrome-changed'));
  }
  /** Single writer for the immersive flag, shared by the header toggle + "Show UI". */
  function setImmersive(on: boolean) {
    settings = { ...settings, immersive: on };
    saveSettings(settings);
    applySettings();
  }
  /** `tab` lets any caller deep-link a settings tab (the header name opens Profile). */
  function openGameSettings(tab?: SettingsTab) {
    openSettings(shell, {
      settings,
      tab,
      username: user.username ?? user.email ?? 'Player',
      // MUST match the key the appearance is stored under (main.ts saves by email and
      // farm/scene.ts reads by email): with user.id the Profile avatar never found a
      // look, and the wardrobe would have written one the world never reads.
      userKey: user.email || user.username || 'terraboma',
      onLogout,
      onChange: (s) => { settings = s; applySettings(); },
    });
  }
  ctx.openSettings = openGameSettings;
  ctx.logout = onLogout;

  // Arm audio here rather than inside a view: the very first gesture may well be a
  // tab click or a settings press, and the browser only lets us start audio on a
  // gesture. Views that need a bed just declare their view id.
  initAudio();
  // One delegated click sound for the whole shell. Wiring `ui_click` into every
  // button by hand would be dozens of edits and would still miss the next button
  // someone adds; a single capture-phase listener covers all of them. `data-nosfx`
  // opts an element out (used by controls that already make their own noise).
  const uiClick = (e: PointerEvent) => {
    const t = e.target as HTMLElement | null;
    const btn = t?.closest?.('button, [role="button"]') as HTMLElement | null;
    if (!btn || btn.hasAttribute('data-nosfx') || (btn as HTMLButtonElement).disabled) return;
    playSfx('ui_click');
  };
  shell.addEventListener('pointerdown', uiClick, true);

  void refreshHud();
  // Come back where you left off (defect 19, rule R1). A reload used to land every
  // player on the farm, so refreshing mid-search threw away the arena screen as well
  // as the search. Through setTab, not applyTab, so the audio/chat wiring is the same
  // as a click.
  setTab(readLastTab() ?? 'farm');
  // First entry after registration: play the origin legend once, over everything.
  void maybeShowLegend();

  // Floating world-chat widget — lives across all tabs (mounted once here) and is
  // reachable from the header on every view.
  chat = mountChat(shell, ctx);
  chat.onUnread((n) => {
    chatBtn.setUnread(n);
    immersiveChatBtn.setUnread(n);
  });
  ctx.toggleChat = (open?: boolean) => chat?.toggle(open);
  applySettings();

  // The landscape gate is NOT created here any more — main.ts owns it for every
  // post-auth view. Mounting it in this function meant the character creator, which
  // the router renders INSTEAD of the game for a new player, had no gate: the first
  // screen after registering was the one screen a phone could open sideways-broken.

  currentTeardown = () => {
    shell.removeEventListener('pointerdown', uiClick, true);
    stopLayoutWatch();
    stopAudio();                      // signing out leaves nothing playing
    chat?.teardown();
    chat = null;
    activeView?.();
    activeView = null;
    // Modals live on document.body, not inside `shell`, so removing the shell left
    // them behind — a sign-out during sleep dropped the "Fast asleep" card onto the
    // marketing site, backdrop, countdown and all. Sweep them, and stop the sleep
    // clock explicitly: its interval is not owned by any node.
    closeSleepOverlay();
    for (const m of document.body.querySelectorAll('.tb-game-modal')) m.remove();
    if (root.contains(shell)) root.removeChild(shell);
  };
  return currentTeardown;
}

/**
 * A chat toggle with its own unread badge. Two of these exist (top bar +
 * immersive overlay) so the chat is one tap away from every view.
 */
function chatButton(className: string, onClick: () => void) {
  const badge = el('span',
    'absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-error text-white text-[10px] font-mono grid place-items-center hidden');
  const node = el('button', className, { title: 'World chat', onClick }, gicon('chat', 16), badge);
  return {
    el: node,
    setUnread: (n: number) => {
      badge.textContent = n > 99 ? '99+' : String(n);
      badge.classList.toggle('hidden', n === 0);
    },
  };
}

function hudChip(iconSrc: string, valueCls: string, opts: { title?: string; onClick?: () => void } = {}) {
  const value = el('span', `font-mono text-sm ${valueCls}`, {}, '…');
  // Pixel-art currency token. Fixed height, nearest-neighbour scaled so it stays
  // crisp next to the mono value.
  // Size lives in app.css (`.tb-hchip img`), NOT inline: an inline declaration beats
  // the stylesheet, which is exactly why the phone rule that unifies the row on 16px
  // was dead code and the tokens kept rendering 18px next to 16px glyphs.
  const icon = el('img', 'tb-hchip-icon shrink-0', { src: iconSrc, alt: '', style: 'image-rendering:pixelated' });
  // Box-less by request: an icon and a number sitting IN the bar, not a bordered
  // widget. `tb-hchip` still carries the shared header sizing (one height, one type
  // scale, one icon size, the 40px touch floor) — only the surface, border and
  // radius are gone, so the row keeps its single baseline. The padding stays: it is
  // now the hit area, since there is no box to grow.
  // `tb-hchip-btn` is what the stylesheet hangs the hover/focus state on — with the
  // border gone there is nothing else left to say "this is tappable".
  const wrap = el('button',
    'tb-hchip tb-hchip-btn flex items-center gap-1.5 px-2 py-1 cursor-pointer',
    { title: opts.title ?? '', onClick: opts.onClick }, icon, value);
  return { value, wrap };
}

/**
 * The XP chip: current level + a thin fill bar toward the next one. Deliberately
 * quiet (gold on wood, no pulse) — this is ambient progress, unlike the farm's
 * urgent red HP bar. Compact on phones: the numeric readout hides under `sm`, but
 * the level and the bar always show, and the title carries the full text.
 */
function xpChip() {
  const lvl = el('span', 'font-mono text-[13px] text-gold leading-none', {}, 'Lv —');
  const fill = el('div', 'h-full bg-gold/80 transition-all', { style: 'width:0%' });
  const track = el('div', 'tb-xp-track w-14 sm:w-24 h-2 rounded-full bg-surface-2 border border-border overflow-hidden', {}, fill);
  // `tb-xp-num` so the phone stylesheet can drop the numerals with the track: they
  // are governed by Tailwind's `sm` (640px MIN-width), which meant a 844px-wide
  // landscape phone showed them and a 390px portrait one did not — the XP chip
  // measured 112px in one orientation and 59px in the other, and at 667x375 the
  // extra 53px pushed the chip straight through the folded tab nav.
  const num = el('span', 'tb-xp-num font-mono text-[11px] text-text-dim leading-none hidden sm:inline', {}, '—');
  // A DIV, and marked `tb-hchip-static`: the 40px floor now comes from `.tb-hchip`
  // itself (it used to be written for `.tb-header button`, which is why the XP chip
  // measured 23px and sat off the row's baseline). Making it a <button> to inherit
  // that floor gave the player a control identical to the two chips that DO open
  // panels but which silently did nothing — on touch there is no hover or title to
  // reveal the difference. Same box, honestly inert.
  // Box-less like the currency chips. The XP TRACK keeps its own frame — that one
  // is the bar itself (a fill needs a trough to be read against), not chrome.
  const wrap = el('div', 'tb-hchip tb-hchip-static flex items-center gap-1.5 px-2 py-1', { title: 'Experience' }, lvl, track, num);
  return {
    wrap,
    /**
     * Draw the SERVER's numbers. Nothing is derived here.
     *
     * This used to compute `xp % 100` itself, which was the flat curve baked into
     * the client — so once the real geometric curve landed, the account screen said
     * "3000 XP to the next level" and this chip still said 100. Two readouts of one
     * number disagreeing is worse than either being wrong alone, because now the
     * player cannot tell which to believe.
     *
     * `span`/`into` come straight from the wallet payload. The fallbacks only cover
     * an older server that does not send them yet.
     */
    set(level: number, xp: number, into?: number, span?: number, isMax?: boolean) {
      const cur = into ?? 0;
      const need = span ?? 0;
      lvl.textContent = `Lv ${level}`;
      if (isMax || need <= 0) {
        // At the cap there is no next level — a 0/0 bar reads as broken.
        fill.style.width = '100%';
        num.textContent = 'MAX';
        wrap.title = `Level ${level} · ${xp.toLocaleString()} XP total`;
        return;
      }
      fill.style.width = `${Math.round((cur / need) * 100)}%`;
      num.textContent = `${cur}/${need}`;
      wrap.title = `Level ${level} · ${need - cur} XP to level ${level + 1}`;
    },
  };
}

function tabClass(active: boolean): string {
  // The layout classes belong HERE, not at the call site: `setTab` reassigns
  // `className` wholesale on every switch, so anything added when the button was
  // built was silently dropped the first time the player changed tab (the icon and
  // its label lost their flex row and stacked).
  return (
    'tb-tab inline-flex items-center gap-1.5 px-4 py-2 text-sm -mb-px ' +
    (active ? 'tb-tab-on' : 'tb-tab-off')
  );
}

export function unmountGame(): void {
  currentTeardown?.();
  currentTeardown = null;
}
